import { faFileAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Yup from "yup";
import { Formik } from "formik";
import { useContext, useEffect, useState } from "react";
import { Form, Alert, Button } from "react-bootstrap";
import { NavLink } from "react-router-dom";
import api from "../../api";
import { imageSet } from "../../pages/icons/AssetIcon/IconList";
import { getUrl } from "../../utils/staticMethods";
import defaultImg from "../../assets/img/icon/GPS/placeholder.png";
import NotyfContext from "../../contexts/NotyfContext";
import useAuth from "../../hooks/useAuth";
import ElementRootFields from "../fields/ElementRootFields";
import ElementCustomFields from "../fields/ElementCustomFields";
import ElementDefaultFields from "../fields/ElementDefaultFields";
import { globalConfig } from "../../config";
import { fieldNameToAssetName } from "../fields/fieldsHelper";

const UserDetails = ({ apiName, asset, setAsset, ...rest }) => {
  // const navigate = useNavigate();
  const notify = useContext(NotyfContext);
  const [imgSrc, setImgSrc] = useState();
  const { user } = useAuth();
  // const [modalTitle, setModalTitle] = useState(null);
  const [customFieldConfig, setCustomFieldConfig] = useState(null);
  const [schema, setSchema] = useState({});

  useEffect(() => {
    if (!user || !asset || !apiName) return;

    if (user.role === "Root") {
      api
        .get(`${apiName || "assets"}/customfieldconfig/` + asset.id)
        .then((res) => {
          setCustomFieldConfig(res.data);
        });
    } else {
      switch (apiName) {
        case "assets":
          setCustomFieldConfig(user.assetFieldConfig);
          break;

        case "people":
          setCustomFieldConfig(user.personFieldConfig);
          break;

        case "zones":
          setCustomFieldConfig(user.zoneFieldConfig);
          break;

        default:
          setCustomFieldConfig(user.assetFieldConfig);
          break;
      }
    }
  }, [user, asset, apiName]);

  useEffect(() => {
    if (!customFieldConfig) return;

    setSchema(
      customFieldConfig.customFields.reduce(
        (previous, value) => {
          let s = Yup.string()
            .max(255)
            .matches(
              /^[^#]*$/,
              "'#' is a protected character used for hashtag searches and cannot be used here"
            );
          if (value.mandatory) {
            s = s.required(value.displayName + " is required");
          }
          let propertyName = fieldNameToAssetName(value.fieldName);
          previous[propertyName] = s;
          return previous;
        },
        {
          email: Yup.string().email("Invalid email"),
        }
      )
    );
  }, [customFieldConfig]);

  useEffect(() => {
    setImgSrc(asset.icon);
  }, [asset.icon]);

  return (
    <>
      <h4>
        <FontAwesomeIcon icon={faFileAlt} className="me-1"></FontAwesomeIcon>{" "}
        Details
      </h4>
      <Formik
        enableReinitialize
        initialValues={{
          primaryName: asset.primaryName || "",
          secondaryName: asset.secondaryName || "",
          reference: asset.reference || "",
          fleetNumber: asset.fleetNumber || "",
          category: asset.category || "",
          suppString1: asset.suppString1 || "",
          suppString2: asset.suppString2 || "",
          suppString3: asset.suppString3 || "",
          suppString4: asset.suppString4 || "",
          suppString5: asset.suppString5 || "",
          email: asset.email || "",
          playSounds: asset.playSounds || false,
          screenAlerts: asset.screenAlerts || false,
          archived: asset.archived || false,
          submit: false,
        }}
        validationSchema={Yup.object().shape(schema)}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          let data = JSON.parse(JSON.stringify(asset));
          data.id = asset.id;
          data.primaryName = values.primaryName;
          data.secondaryName = values.secondaryName;
          data.reference = values.reference;
          data.fleetNumber = values.fleetNumber;
          data.category = values.category;
          data.suppString1 = values.suppString1;
          data.suppString2 = values.suppString2;
          data.suppString3 = values.suppString3;
          data.suppString4 = values.suppString4;
          data.suppString5 = values.suppString5;
          data.icon = imgSrc;
          if (apiName === "assets") {
            data.archived = values.archived;
          }
          if (apiName === "people") {
            data.email = values.email;
          }
          if (asset.id === user?.id) {
            data.playSounds = values.playSounds;
            data.screenAlerts = values.screenAlerts;
          }
          try {
            api.put(`${apiName || "assets"}`, data).then((res) => {
              notify.open({
                type: "success",
                message: "Changes Saved",
              });
              setAsset(res.data);
            });
          } catch (error) {
            const message = error.message || "Something went wrong";
            setStatus({ success: false });
            setErrors({ submit: message });
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
        }) => (
          <Form onSubmit={handleSubmit}>
            {errors.submit && (
              <Alert className="my-3" variant="danger">
                <div className="alert-message">{errors.submit}</div>
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Icon</Form.Label>
              <div>
                {user?.editPeople ? (
                  <NavLink
                    to={{
                      pathname: "/icons/asset-icon",
                      search: `?id=${asset.id}&type=people`,
                    }}
                  >
                    <img
                      alt={imgSrc}
                      className="profile-icon"
                      src={
                        imageSet[getUrl(imgSrc)]
                          ? require("./../../assets/img/icon/" +
                              imageSet[getUrl(imgSrc)]).default
                          : defaultImg
                      }
                    />
                  </NavLink>
                ) : (
                  <img
                    alt={imgSrc}
                    className="profile-icon"
                    src={
                      imageSet[getUrl(imgSrc)]
                        ? require("./../../assets/img/icon/" +
                            imageSet[getUrl(imgSrc)]).default
                        : defaultImg
                    }
                  />
                )}
              </div>
            </Form.Group>

            {customFieldConfig && customFieldConfig.enabled ? (
              user.role === "Root" ? (
                <ElementRootFields
                  mode={"details"}
                  apiName={apiName}
                  asset={asset}
                  setAsset={setAsset}
                  customFieldConfig={customFieldConfig}
                  {...rest}
                />
              ) : (
                <ElementCustomFields
                  mode={"details"}
                  apiName={apiName}
                  asset={asset}
                  setAsset={setAsset}
                  customFieldConfig={customFieldConfig}
                  {...rest}
                />
              )
            ) : (
              <ElementDefaultFields
                mode={"details"}
                apiName={apiName}
                asset={asset}
                setAsset={setAsset}
                {...rest}
              />
            )}

            {apiName === "assets" &&
              user?.role !== "User" &&
              globalConfig.applicationTheme !== "srl" && (
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    name="archived"
                    label="Archived"
                    checked={values.archived}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Form.Group>
              )}

            <div className="mt-3">
              {(user?.editPeople || user?.id === asset?.id) && (
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  Save
                </Button>
              )}
              {/* {((props.type === "zones" && user?.deleteZones) ||
                (props.type === "assets" && user?.deleteAssets) ||
                (props.type === "people" && user?.deletePeople)) &&
                user?.id !== asset?.id && (
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() => setModalTitle(true)}
                    className="m-lg-1"
                  >
                    Delete
                  </Button>
                )} */}
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default UserDetails;
